import { OAuthProvider } from '@seapoint/server/enums';
import { Mail, MailCheck, Send } from 'lucide-react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { OAuthProviderLogoIcon } from '../oauth-provider-logo-icon';

type Variant = 'EMAIL' | 'FORWARD';

type CardVariantConfig =
  | {
      variant: Extract<Variant, 'EMAIL'>;
      onEmailConnect: () => void;
    }
  | {
      variant: Extract<Variant, 'FORWARD'>;
      forwardingEmail: string;
      onForwardEmailClick: () => void;
    };

type InvoiceIntakeCardProps = {
  cardVariantConfig: CardVariantConfig;
};

export const InvoiceIntakeCard = ({ cardVariantConfig }: InvoiceIntakeCardProps) => {
  const { icon: Icon, title, description, ctaIcon: CtaIcon, ctaText, onCtaClick } = getCardContent(cardVariantConfig);

  return (
    <Inline
      space={4}
      align="start"
      wrap="nowrap"
      className="w-full rounded-xl border-[0.5px] border-border bg-white px-6 py-5"
    >
      <div className="h-10 w-10 rounded-full bg-muted p-2.5">
        <Icon className="text-black" strokeWidth={1} />
      </div>

      <Stack space={4}>
        <Stack space={2} align="stretch">
          <h3 className="text-lg font-medium tracking-tight text-black">{title}</h3>

          <p className="text-sm leading-6 font-medium tracking-tight text-muted-foreground">{description}</p>
        </Stack>

        <Button variant="secondary" iconLeft={CtaIcon} onClick={onCtaClick}>
          {ctaText}
        </Button>
      </Stack>
    </Inline>
  );
};

const getCardContent = (cardVariantConfig: CardVariantConfig) => {
  const { variant } = cardVariantConfig;

  const baseStyles = {
    strokeWidth: 1,
    className: 'text-black',
  };

  switch (variant) {
    case 'EMAIL':
      return {
        icon: MailCheck,
        title: 'Connect Gmail',
        description: `We'll scan your inbox and automatically pull any invoices received into Seapoint.`,
        ctaIcon: <OAuthProviderLogoIcon provider={OAuthProvider.GMAIL} />,
        ctaText: 'Connect Gmail',
        onCtaClick: cardVariantConfig.onEmailConnect,
      };
    case 'FORWARD':
      return {
        icon: Send,
        title: 'Forward invoices',
        description: `Send invoices your company's own Seapoint address (click to copy email address):`,
        ctaIcon: <Mail {...baseStyles} />,
        ctaText: cardVariantConfig.forwardingEmail,
        onCtaClick: cardVariantConfig.onForwardEmailClick,
      };
    default:
      return exhaustiveCheck(variant);
  }
};
